import React, { createContext, useContext, useMemo } from 'react';
import { Platform } from 'react-native';

import NativeReviewService from './NativeReviewService';
import NullReviewService from './NullReviewService';
import { ReviewService } from './ReviewService';

/**
 * Context provider for review services.
 */
export const ReviewServiceContext = createContext<ReviewService | Error>(
  new Error('ReviewServiceContext must be provided before using context')
);

/**
 * Custom hook to access ReviewService from context.
 * Wraps built-in hook to ensure that context has been provided.
 */
export const useReviewServiceContext = (): ReviewService => {
  const context = useContext(ReviewServiceContext);
  if (context instanceof Error) {
    throw context;
  }
  return context;
};

/**
 * Props for providing review services.
 */
interface ReviewServiceContextProviderProps {
  /**
   * Optional child nodes that will use the context.
   */
  children?: React.ReactNode;

  /**
   * Optional service override for testing purposes.
   * When provided, this service will be used instead of platform-based selection.
   */
  serviceOverride?: ReviewService;
}

/**
 * Provider for review services.
 * Automatically selects appropriate service implementation based on platform.
 */
export const ReviewServiceContextProvider = function ReviewServiceContextProvider(
  props: ReviewServiceContextProviderProps
) {
  const { children, serviceOverride } = props;
  const service = useMemo(() => {
    try {
      if (serviceOverride) {
        return serviceOverride;
      }
      if (Platform.OS === 'web') {
        return new NullReviewService();
      }
      return new NativeReviewService();
    } catch (cause) {
      throw new Error(`ReviewService failed to initialize for platform: ${Platform.OS}`, { cause });
    }
  }, [serviceOverride]);
  return <ReviewServiceContext.Provider value={service}>{children}</ReviewServiceContext.Provider>;
};
